import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AgGridReact,
  ColDef,
  DomLayout,
  GrcButton,
  GrcButtonStylingMode,
  GrcButtonType,
  GrcGrid,
  GrcNotify,
  NotifyType,
  ServerColumnOptions,
  gridOptions
} from '@grc/ui-package';
import useTenants from './useTenants';
import TenantCreateDialog from './tenant-create-dialog';
import TenantEditDrawer from './tenant-edit-drawer';
import { TenantDetails } from './tenant-types';
import { subscribeToNotifications } from '../services/notifications-hub';
import './tenants.scss';

// context.server declares what GET /v2/tenant filters and sorts on (GrcGrid serverPaging).
type TenantColumn = ColDef<TenantDetails> & { context?: { server: ServerColumnOptions } };

interface TenantActivatedNotification {
  tenantId: string;
  name: string;
}

export default function Tenants() {
  const {
    serverPaging,
    createOpen,
    editTenant,
    openCreate,
    closeCreate,
    openEdit,
    closeEdit,
    createTenant,
    updateTenant,
    activateTenant
  } = useTenants();
  const gridRef = useRef<AgGridReact>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    subscribeToNotifications(envelope => {
      if (envelope.type === 'tenant_activated_notification') {
        const data = envelope.data as TenantActivatedNotification;
        GrcNotify({
          status: NotifyType.Success,
          message: `Tenant "${data.name}" activated.`
        });
      }
    }).then(u => { unsubscribe = u; });
    return () => { unsubscribe?.(); };
  }, []);

  const columnDefs: TenantColumn[] = [
    {
      headerName: 'Name',
      field: 'name',
      flex: 1.5,
      context: { server: { filterParam: 'name', sortField: 'name' } },
      pinned: 'left',
      cellRenderer: (params: { data?: TenantDetails; value: string }) => {
        // Same treatment as the Usage column: a real button, so it is focusable and
        // keyboard-operable. AG Grid leaves data absent on group and footer rows.
        const tenant = params.data;
        if (!tenant) return null;

        return (
          <GrcButton
            id={`tenant-open-${tenant.id}`}
            text={params.value}
            type={GrcButtonType.Default}
            stylingMode={GrcButtonStylingMode.Text}
            onClick={() => openEdit(tenant)}
          />
        );
      }
    },
    // Hidden by default rather than dropped: the columns tool panel that gridOptions
    // already ships turns each of them back on, so the default view stays readable
    // without deciding for anyone which nine columns they need.
    { headerName: 'Tenant ID', field: 'id', flex: 1.6, hide: true },
    { headerName: 'City', field: 'city', flex: 1, context: { server: { filterParam: 'city', sortField: 'city' } } },
    { headerName: 'State', field: 'state', flex: 0.7, context: { server: { filterParam: 'state', sortField: 'state' } } },
    { headerName: 'Country', field: 'country', flex: 0.8, hide: true, context: { server: { filterParam: 'country', sortField: 'country' } } },
    { headerName: 'Email', field: 'email', flex: 1.2, hide: true, context: { server: { filterParam: 'email', sortField: 'email' } } },
    { headerName: 'Phone', field: 'phoneNumber', flex: 1, hide: true },
    {
      headerName: 'Active',
      field: 'active',
      flex: 0.5,
      context: { server: { filterParam: 'active', filterType: 'set' as const, sortField: 'active' } },
      filterParams: {
        values: ['true', 'false'],
        valueFormatter: (params: { value: string }) => (params.value === 'true' ? 'Yes' : 'No')
      },
      valueFormatter: params => (params.value ? 'Yes' : 'No')
    },
    {
      headerName: 'Usage',
      flex: 0.5,
      sortable: false,
      filter: false,
      cellRenderer: (params: { data?: TenantDetails }) => {
        // Bound once so the callback closes over a narrowed value; AG Grid leaves data
        // absent on group and footer rows.
        const tenant = params.data;
        if (!tenant) return null;

        return (
          <GrcButton
            id={`tenant-usage-${tenant.id}`}
            text="View"
            type={GrcButtonType.Default}
            stylingMode={GrcButtonStylingMode.Text}
            onClick={() => navigate(`/tenants/${tenant.id}/usage`)}
          />
        );
      }
    }
  ];

  return (
    <div className="tenants-page">
      <div className="tenants-toolbar">
        <GrcButton
          id="tenants-create-button"
          text="Create Tenant"
          type={GrcButtonType.Default}
          stylingMode={GrcButtonStylingMode.Contained}
          onClick={openCreate}
        />
      </div>

      <div className="tenants-grid ag-theme-alpine">
        <GrcGrid
          gridRef={gridRef}
          colDefs={columnDefs}
          gridOptions={gridOptions}
          domLayout={DomLayout.Height}
          serverPaging={serverPaging}
        />
      </div>

      <TenantCreateDialog open={createOpen} onCancel={closeCreate} onCreate={createTenant} />

      <TenantEditDrawer
        tenant={editTenant}
        onCancel={closeEdit}
        onUpdate={updateTenant}
        onActivate={activateTenant}
      />
    </div>
  );
}
